/**
 * Whose turn is it?
 *
 * Every order status belongs to exactly one role — the one that has to do
 * something next. This drives the badge on the Home tab so a merchant with
 * four quotes waiting sees it from any screen, rather than only discovering
 * it if they happen to open the right page.
 *
 * Taken from the Mobile v3 design's `TURN` map, which is the product's own
 * statement of who owns each state. The badge render path already existed in
 * PortalShell; nothing was driving it, so five pages had shipped hardcoded
 * counts instead.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requests, merchantStats, type ServiceRequestWithItems } from './api';
import type { NavSection } from '../components/shared/PortalShell';

export type TurnRole = 'customer' | 'merchant' | 'recipient' | 'admin';

/** Status → the role that has to act on it. */
const TURN: Record<string, TurnRole> = {
  submitted: 'merchant',
  quote_pending: 'merchant',
  quoted: 'customer',
  customer_approval_pending: 'customer',
  payment_pending: 'customer',
  paid: 'merchant',
  assigned_to_merchant: 'merchant',
  in_progress: 'merchant',
  ready_for_delivery: 'merchant',
  delivered: 'recipient',
  disputed: 'admin',
  // `confirmed` is deliberately absent: the order is done. The admin payout
  // release is tracked on its own queue, not by badging every finished order.
};

/** How many of these requests are waiting on `role`. */
export function countPending(rows: ServiceRequestWithItems[], role: TurnRole): number {
  return rows.reduce((n, r) => (TURN[r.status] === role ? n + 1 : n), 0);
}

/**
 * Attach a badge to the Home/Dashboard item when the signed-in role owes
 * something.
 *
 * Returns the nav unchanged when the count is zero, so an idle role gets no
 * decoration — and never a "0", which reads as a broken badge rather than as
 * nothing to do.
 */
export function withHomeBadge(nav: NavSection[], count: number, homeId: string): NavSection[] {
  if (count <= 0) return nav;

  return nav.map((section) => ({
    ...section,
    items: section.items.map((item) =>
      item.id === homeId ? { ...item, badge: count } : item),
  }));
}

/** Customer nav with a live count of quotes to approve or pay for. */
export function useCustomerNav(nav: NavSection[]): NavSection[] {
  const q = useQuery({
    queryKey: ['me', 'requests'],
    queryFn: () => requests.list(),
    // Shares the cache with the dashboard's own query, so adding the badge
    // costs no extra round trip on the page that matters most.
    staleTime: 30_000,
  });

  const count = useMemo(() => countPending(q.data ?? [], 'customer'), [q.data]);

  return useMemo(() => withHomeBadge(nav, count, 'dashboard'), [nav, count]);
}

/** Recipient nav, badged when a delivery is waiting on their code. */
export function useRecipientNav(nav: NavSection[]): NavSection[] {
  const q = useQuery({
    queryKey: ['recipient', 'requests'],
    queryFn: () => requests.list(),
    staleTime: 30_000,
  });

  const count = useMemo(() => countPending(q.data ?? [], 'recipient'), [q.data]);

  return useMemo(() => withHomeBadge(nav, count, 'home'), [nav, count]);
}

/**
 * Merchant nav, badged with quotes owed plus orders to move.
 *
 * Uses the stats endpoint rather than listing every request: the merchant's
 * own dashboard already fetches it, and the server counts far more cheaply
 * than shipping the whole list to a phone on 3G.
 */
export function useMerchantNav(nav: NavSection[]): NavSection[] {
  const q = useQuery({
    queryKey: ['merchant', 'overview'],
    queryFn: merchantStats.overview,
    staleTime: 30_000,
  });

  const count = (q.data?.stats.awaiting_quote ?? 0) + (q.data?.stats.to_prepare ?? 0);

  return useMemo(() => withHomeBadge(nav, count, 'dashboard'), [nav, count]);
}
